import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button";
import { Input } from "./Input";
import { Field } from "./Field";

const FEATURES = ["Margins", "Lily pads", "Island", "Open water", "Reeds", "Overhanging trees", "Other"] as const;
const BOTTOMS = ["Not sure", "Silt", "Gravel", "Weed", "Clay", "Sand", "Hard bottom", "Other"] as const;
const SHAPES = ["Not sure", "Flat", "Drop-off", "Plateau", "Slope"] as const;

export function LiveSaveSpot({ lakeId }: { lakeId: number | string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    shape: "Not sure",
    distance_m: "",
    depth_ft: "",
    substrate: "",
    feature_description: "",
    private_notes: "",
  });

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form.feature_description.trim()) { setMessage("Choose where your bait is fishing."); return; }
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/lakes/${lakeId}/spots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim() || [form.feature_description, form.substrate !== "Not sure" ? form.substrate : ""].filter(Boolean).join(" · "),
          distance_m: form.distance_m || null,
          depth_ft: form.depth_ft || null,
          substrate: form.substrate || null,
          feature_description: [form.feature_description, form.shape !== "Not sure" ? `Bottom shape: ${form.shape}` : ""].filter(Boolean).join(" · ") || null,
          private_notes: form.private_notes || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save spot");
      await queryClient.invalidateQueries({ queryKey: ["carpintel_saved_spots", Number(lakeId)] });
      setForm({ name: "", shape: "Not sure", distance_m: "", depth_ft: "", substrate: "", feature_description: "", private_notes: "" });
      setOpen(false);
      setMessage("Spot saved and ready to use when logging a catch");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not save spot");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-raised p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">Saved spots</h2>
          <p className="mt-1 text-sm leading-5 text-secondary">
            Save where you are fishing. Rod tackle stays separate in Rod setups.
          </p>
        </div>
        {!open ? <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Save spot</Button> : null}
      </div>
      {open ? (
        <form onSubmit={save} className="mt-4 space-y-3 border-t border-border pt-4">
          <p className="text-sm text-secondary">Choose where your bait is fishing. Exact distances and coordinates are not needed.</p>
          <Field label="Where is your bait?" id="live-spot-feature">
            <select id="live-spot-feature" required className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary" value={form.feature_description} onChange={(e) => setForm((f) => ({ ...f, feature_description: e.target.value }))}>
              <option value="">Choose a feature</option>
              {FEATURES.map((feature) => <option key={feature} value={feature}>{feature}</option>)}
            </select>
          </Field>
          <Field label="What's the lakebed like?" id="live-spot-substrate">
            <select id="live-spot-substrate" className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary" value={form.substrate || "Not sure"} onChange={(e) => setForm((f) => ({ ...f, substrate: e.target.value }))}>
              {BOTTOMS.map((bottom) => <option key={bottom} value={bottom}>{bottom}</option>)}
            </select>
          </Field>
          <Field label="Bottom shape (optional)" id="live-spot-shape">
            <select id="live-spot-shape" className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary" value={form.shape} onChange={(e) => setForm((f) => ({ ...f, shape: e.target.value }))}>
              {SHAPES.map((shape) => <option key={shape} value={shape}>{shape}</option>)}
            </select>
          </Field>
          <details className="rounded-lg border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium text-primary">More details (optional)</summary>
            <div className="mt-3 space-y-3">
              <Field label="Spot name (optional)" id="live-spot-name">
                <Input id="live-spot-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Automatically named if left blank" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Distance (m)" id="live-spot-distance">
                  <Input id="live-spot-distance" type="number" min="0" step="0.5" value={form.distance_m} onChange={(e) => setForm((f) => ({ ...f, distance_m: e.target.value }))} />
                </Field>
                <Field label="Depth (ft)" id="live-spot-depth">
                  <Input id="live-spot-depth" type="number" min="0" step="0.1" value={form.depth_ft} onChange={(e) => setForm((f) => ({ ...f, depth_ft: e.target.value }))} />
                </Field>
              </div>
            </div>
          </details>
          <Field label="Private notes" id="live-spot-notes">
            <Input id="live-spot-notes" value={form.private_notes} onChange={(e) => setForm((f) => ({ ...f, private_notes: e.target.value }))} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" isLoading={saving}>Save spot</Button>
            <Button type="button" size="sm" variant="tertiary" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </form>
      ) : null}
      {message ? <p className="mt-3 text-sm text-secondary" role="status">{message}</p> : null}
    </section>
  );
}
